import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import { generateUUID32Hex } from '../../../utils/uuid';
import {
  deleteFoundationAnswerGroup,
  getFoundationFormDefinitions,
  type FoundationFormAnswerInput,
  type FoundationFormQuestion,
} from '../../../endpoints/foundation';

type Props = {
  formKind: string;
  academicYear: string;
  value: FoundationFormAnswerInput[];
  onChange: (next: FoundationFormAnswerInput[]) => void;
  applicationId?: string;
  disabled?: boolean;
  title?: string;
};

function normalizeMatch(v: string): string {
  return String(v || '')
    .trim()
    .toLowerCase();
}

function matchesConditional(parentValue: string, expected?: string): boolean {
  if (!expected) return true;
  const p = normalizeMatch(parentValue);
  const e = normalizeMatch(expected);
  if (p === e) return true;
  if (e === 'yes' && (p === 'true' || p === '1')) return true;
  if (e === 'true' && (p === 'yes' || p === '1')) return true;
  if (e === '1' && (p === 'yes' || p === 'true')) return true;
  if (e === 'no' && (p === 'false' || p === '0')) return true;
  return false;
}

function answerValue(
  answers: FoundationFormAnswerInput[],
  key: string,
  groupId = ''
): string {
  const row = answers.find(
    (a) =>
      a.question_key === key && (a.answer_group_id || '') === (groupId || '')
  );
  return row?.value ?? '';
}

function upsertAnswer(
  answers: FoundationFormAnswerInput[],
  questionKey: string,
  value: string,
  groupId = ''
): FoundationFormAnswerInput[] {
  const next = answers.filter(
    (a) =>
      !(
        a.question_key === questionKey &&
        (a.answer_group_id || '') === (groupId || '')
      )
  );
  const item: FoundationFormAnswerInput = { question_key: questionKey, value };
  if (groupId) item.answer_group_id = groupId;
  next.push(item);
  return next;
}

function optionList(q: FoundationFormQuestion): string[] {
  const raw = q.options_json as Record<string, unknown> | unknown[] | undefined;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((o) => String(o));
  }
  if (Array.isArray(raw.options)) {
    return (raw.options as unknown[]).map((o) => String(o));
  }
  return Object.values(raw).map((o) => String(o));
}

function QuestionField({
  question,
  value,
  onChange,
  disabled,
}: {
  question: FoundationFormQuestion;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const type = question.answer_type;
  const label = question.label;
  const helper = question.help_text;

  if (type === 'boolean') {
    return (
      <TextField
        select
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        helperText={helper}
        required={!!question.required}
        disabled={disabled}
        fullWidth
      >
        <MenuItem value="yes">Yes</MenuItem>
        <MenuItem value="no">No</MenuItem>
      </TextField>
    );
  }
  if (type === 'textarea') {
    return (
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        helperText={helper}
        required={!!question.required}
        disabled={disabled}
        multiline
        minRows={3}
        fullWidth
      />
    );
  }
  if (type === 'number') {
    return (
      <TextField
        label={label}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        helperText={helper}
        required={!!question.required}
        disabled={disabled}
        fullWidth
      />
    );
  }
  if (type === 'date') {
    return (
      <TextField
        label={label}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        helperText={helper}
        required={!!question.required}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        fullWidth
      />
    );
  }
  if (type === 'single_select') {
    const opts = optionList(question);
    return (
      <TextField
        select
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        helperText={helper}
        required={!!question.required}
        disabled={disabled}
        fullWidth
      >
        {opts.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  if (type === 'multi_select') {
    const opts = optionList(question);
    const selected = value ? value.split(',').map((s) => s.trim()) : [];
    return (
      <TextField
        select
        label={label}
        value={selected}
        onChange={(e) => {
          const v = e.target.value;
          onChange(Array.isArray(v) ? v.join(',') : String(v));
        }}
        helperText={helper}
        required={!!question.required}
        disabled={disabled}
        SelectProps={{ multiple: true }}
        fullWidth
      >
        {opts.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText={helper}
      required={!!question.required}
      disabled={disabled}
      fullWidth
    />
  );
}

export default function FoundationFormRenderer({
  formKind,
  academicYear,
  value,
  onChange,
  applicationId,
  disabled = false,
  title,
}: Props) {
  const { data, isLoading } = useApiGet(
    ['foundation-form-def', formKind, academicYear],
    () =>
      getFoundationFormDefinitions({
        form_kind: formKind,
        academic_year: academicYear,
      }),
    { enabled: !!formKind && !!academicYear, retry: 1 }
  );

  const questions = useMemo(() => {
    const def = (data?.items || [])[0];
    return [...(def?.questions || [])].sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
    );
  }, [data]);

  const byId = useMemo(() => {
    const m = new Map<string, FoundationFormQuestion>();
    questions.forEach((q) => m.set(q.id, q));
    return m;
  }, [questions]);

  const roots = questions.filter((q) => !q.parent_question_id);
  const childrenOf = (parentId: string) =>
    questions.filter((q) => q.parent_question_id === parentId);

  const groupIdsForParent = (parent: FoundationFormQuestion): string[] => {
    const kids = childrenOf(parent.id);
    const keys = new Set(kids.map((k) => k.question_key));
    const ids = new Set<string>();
    value.forEach((a) => {
      if (keys.has(a.question_key) && a.answer_group_id) {
        ids.add(a.answer_group_id);
      }
    });
    return Array.from(ids);
  };

  const setAnswer = (key: string, v: string, groupId = '') => {
    onChange(upsertAnswer(value, key, v, groupId));
  };

  const addGroup = (parent: FoundationFormQuestion) => {
    const groupId = generateUUID32Hex();
    let next = value;
    childrenOf(parent.id).forEach((child) => {
      next = upsertAnswer(next, child.question_key, '', groupId);
    });
    onChange(next);
  };

  const removeGroup = async (
    parent: FoundationFormQuestion,
    groupId: string
  ) => {
    const kidKeys = new Set(childrenOf(parent.id).map((c) => c.question_key));
    const next = value.filter(
      (a) => !(a.answer_group_id === groupId && kidKeys.has(a.question_key))
    );
    onChange(next);
    if (applicationId) {
      try {
        await deleteFoundationAnswerGroup(applicationId, formKind, groupId);
      } catch {
        // Local state already updated; refresh on next save/load if delete fails.
      }
    }
  };

  const shouldShowChild = (
    child: FoundationFormQuestion,
    groupId = ''
  ): boolean => {
    if (!child.is_conditional || !child.parent_question_id) return true;
    const parent = byId.get(child.parent_question_id);
    if (!parent) return true;
    const parentVal = answerValue(
      value,
      parent.question_key,
      parent.supports_repeating_groups ? groupId : ''
    );
    return matchesConditional(parentVal, child.conditional_value);
  };

  if (isLoading) {
    return <Typography variant="body2">Loading form…</Typography>;
  }
  if (!questions.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No {formKind} form definition for {academicYear}.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {title && <Typography variant="subtitle1">{title}</Typography>}
      {roots.map((q) => {
        const repeating = !!q.supports_repeating_groups;
        const kids = childrenOf(q.id);

        return (
          <Box key={q.id}>
            <QuestionField
              question={q}
              value={answerValue(value, q.question_key)}
              onChange={(v) => setAnswer(q.question_key, v)}
              disabled={disabled}
            />

            {!repeating &&
              kids
                .filter((c) => shouldShowChild(c))
                .map((c) => (
                  <Box key={c.id} sx={{ mt: 2, pl: 2 }}>
                    <QuestionField
                      question={c}
                      value={answerValue(value, c.question_key)}
                      onChange={(v) => setAnswer(c.question_key, v)}
                      disabled={disabled}
                    />
                  </Box>
                ))}

            {repeating && (
              <Stack spacing={2} sx={{ mt: 2, pl: 1 }}>
                {groupIdsForParent(q).map((groupId, idx) => (
                  <Box
                    key={groupId}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="body2">
                        {q.label} — entry {idx + 1}
                      </Typography>
                      {!disabled && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeGroup(q, groupId)}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                    {kids
                      .filter((c) => shouldShowChild(c, groupId))
                      .map((c) => (
                        <Box key={c.id} sx={{ mt: 1 }}>
                          <QuestionField
                            question={c}
                            value={answerValue(value, c.question_key, groupId)}
                            onChange={(v) =>
                              setAnswer(c.question_key, v, groupId)
                            }
                            disabled={disabled}
                          />
                        </Box>
                      ))}
                  </Box>
                ))}
                {!disabled && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => addGroup(q)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Add {q.label} entry
                  </Button>
                )}
              </Stack>
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        );
      })}
    </Stack>
  );
}
